import { Input, Checkbox, Tooltip } from "antd";
import { Plus, Sparkles, X } from "lucide-react";
import { nanoid } from "nanoid";
import { useState } from "react";
import { RESUME_SECTION_TYPES } from "../../../optimizeResume/editor/config";
import SectionCardShell from "../../../optimizeResume/editor/SectionCardShell";
import WriteWithAiBulletsModal from "../../modals/WriteWithAiBulletsModal";

const { TextArea } = Input;

const blankItem = () => ({
  id: `proj-${nanoid(6)}`,
  name: "",
  sourceUrl: "",
  description: "",
  startDate: "",
  endDate: "",
  current: false,
  highlights: [],
});

export default function Projects({
  id,
  data,
  onChange,
  onDelete,
  allCollapsed,
}) {
  const items = data ?? [];
  const [aiBulletsIdx, setAiBulletsIdx] = useState(null);

  const updateItem = (idx, patch) => {
    onChange(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  const removeItem = (idx) => {
    onChange(items.filter((_, i) => i !== idx));
  };

  const addItem = () => {
    onChange([...items, blankItem()]);
  };

  const updateBullet = (idx, bIdx, val) => {
    onChange(
      items.map((it, i) =>
        i === idx
          ? {
              ...it,
              highlights: (it.highlights ?? []).map((b, j) =>
                j === bIdx ? val : b,
              ),
            }
          : it,
      ),
    );
  };

  const addBullet = (idx) => {
    onChange(
      items.map((it, i) =>
        i === idx
          ? {
              ...it,
              highlights: [...(it.highlights ?? []), ""],
            }
          : it,
      ),
    );
  };

  const removeBullet = (idx, bIdx) => {
    onChange(
      items.map((it, i) =>
        i === idx
          ? {
              ...it,
              highlights: (it.highlights ?? []).filter((_, j) => j !== bIdx),
            }
          : it,
      ),
    );
  };

  return (
    <SectionCardShell
      id={id}
      type={RESUME_SECTION_TYPES.PROJECTS}
      data={data}
      onChange={onChange}
      onDelete={onDelete}
      allCollapsed={allCollapsed}
    >
      <div className="flex flex-col gap-4">
        {items.map((it, idx) => (
          <div
            key={it.id}
            className="border border-gray-200 rounded-lg p-3 bg-gray-50/40"
          >
            <div className="flex justify-between items-start mb-2">
              <p className="font-medium text-sm text-[#344054]">
                {it.name || `Project #${idx + 1}`}
              </p>
              <button
                onClick={() => removeItem(idx)}
                className="text-gray-400 hover:text-red-600"
              >
                <X size={16} />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="Project name"
                value={it.name}
                onChange={(e) => updateItem(idx, { name: e.target.value })}
              />
              <Input
                placeholder="Link"
                value={it.sourceUrl}
                onChange={(e) => updateItem(idx, { sourceUrl: e.target.value })}
              />
              <Input
                placeholder="Start (YYYY-MM)"
                value={it.startDate}
                onChange={(e) => updateItem(idx, { startDate: e.target.value })}
              />
              <Input
                placeholder="End (YYYY-MM)"
                value={it.endDate}
                disabled={it.current}
                onChange={(e) => updateItem(idx, { endDate: e.target.value })}
              />
              <div className="col-span-2 flex items-center gap-2">
                <Checkbox
                  checked={it.current}
                  onChange={(e) =>
                    updateItem(idx, {
                      current: e.target.checked,
                      endDate: e.target.checked ? "" : it.endDate,
                    })
                  }
                >
                  Ongoing
                </Checkbox>
              </div>
              <div className="col-span-2">
                <TextArea
                  placeholder="Short description"
                  value={it.description}
                  autoSize={{ minRows: 1, maxRows: 3 }}
                  onChange={(e) =>
                    updateItem(idx, { description: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="mt-3 border border-dashed border-[#FEDF89] rounded-lg p-3 bg-[#FFFAEB]/40">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-[#667085] mb-0">
                  Bullets
                </p>
                <Tooltip
                  overlayInnerStyle={{
                    borderRadius: "6px",
                    padding: "6px 10px",
                    backgroundColor: "#0C111D",
                    color: "#fff",
                  }}
                  title="Generate bullets with AI"
                >
                  <button
                    type="button"
                    onClick={() => setAiBulletsIdx(idx)}
                    className="flex items-center gap-2 text-[.75rem] font-medium text-[#DC6803] hover:text-amber-500 border border-[#FEDF89] bg-[#FFFAEB] rounded-full px-2 py-[.2rem] transition-all duration-200"
                  >
                    <Sparkles size={14.5} className="" />
                    Write with AI
                  </button>
                </Tooltip>
              </div>
              <div className="flex flex-col gap-2">
                {(it.highlights ?? []).map((b, bIdx) => (
                  <div key={bIdx} className="flex gap-2 items-start">
                    <TextArea
                      value={b}
                      autoSize={{ minRows: 1, maxRows: 3 }}
                      onChange={(e) => updateBullet(idx, bIdx, e.target.value)}
                    />
                    <button
                      onClick={() => removeBullet(idx, bIdx)}
                      className="text-gray-400 hover:text-red-600 mt-2"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => addBullet(idx)}
                  className="self-start flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                >
                  <Plus size={14} /> Add bullet
                </button>
              </div>
            </div>
          </div>
        ))}
        <button
          onClick={addItem}
          className="self-start flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
        >
          <Plus size={14} /> Add project
        </button>
      </div>

      <WriteWithAiBulletsModal
        open={aiBulletsIdx !== null}
        setOpen={(v) => setAiBulletsIdx(v ? aiBulletsIdx : null)}
        item={aiBulletsIdx !== null ? items[aiBulletsIdx] : null}
        bulletsField="highlights"
        sectionKey="projects"
        infoContent={
          aiBulletsIdx !== null
            ? (() => {
                const it = items[aiBulletsIdx] ?? {};
                const start = it.startDate || "";
                const end = it.current ? "Ongoing" : it.endDate || "";
                const dateRange =
                  start && end ? `${start} – ${end}` : start || end;
                return (
                  <div className="flex flex-col gap-[.15rem]">
                    <p className="text-[.9rem] font-semibold text-[#0F1B23] mb-0">
                      {it.name || "—"}
                    </p>
                    {it.sourceUrl && (
                      <p className="text-[.8rem] text-[#475467] mb-0 break-all">
                        {it.sourceUrl}
                      </p>
                    )}
                    {dateRange && (
                      <p className="text-[.75rem] text-[#667085] mb-0">
                        {dateRange}
                      </p>
                    )}
                    {it.description && (
                      <p className="text-[.8rem] text-[#475467] mt-1 mb-0">
                        {it.description}
                      </p>
                    )}
                  </div>
                );
              })()
            : null
        }
        onApply={(bullets) => {
          if (aiBulletsIdx !== null) {
            updateItem(aiBulletsIdx, { highlights: bullets });
          }
        }}
      />
    </SectionCardShell>
  );
}
